// frontend/src/components/TotpChallengeForm.jsx
import { useState, useRef, useEffect } from 'react';
import BrandIcon from './BrandIcon';

// ── Écran de saisie du code TOTP (étape 2 du login) ─────────────
// Réutilisé par /login (comptes tenants) et /superadmin.
// props :
//   onSubmit(code, isBackupCode, remember) — appelé à la validation
//   onBack() — "Retour à la connexion"
//   loading, error
//   title, subtitle
export default function TotpChallengeForm({ onSubmit, onBack, loading, error, title, subtitle }) {
  const [digits, setDigits]   = useState(['', '', '', '', '', '']);
  const [remember, setRemember] = useState(false);
  const [useBackup, setUseBackup] = useState(false);
  const [backupCode, setBackupCode] = useState('');
  const inputRefs = useRef([]);
  const submittedRef = useRef(false);

  useEffect(() => {
    inputRefs.current[0]?.focus();
  }, [useBackup]);

  useEffect(() => {
    // Réinitialiser les champs quand une erreur arrive (nouvelle tentative)
    if (error) {
      setDigits(['', '', '', '', '', '']);
      submittedRef.current = false;
      inputRefs.current[0]?.focus();
    }
  }, [error]);

  const submitCode = (code) => {
    if (submittedRef.current) return;
    submittedRef.current = true;
    onSubmit(code, false, remember);
  };

  const handleChange = (i, val) => {
    const v = val.replace(/[^0-9]/g, '').slice(-1);
    const next = [...digits];
    next[i] = v;
    setDigits(next);
    if (v && i < 5) inputRefs.current[i + 1]?.focus();
    const joined = next.join('');
    if (joined.length === 6 && next.every(d => d !== '')) submitCode(joined);
  };

  const handleKeyDown = (i, e) => {
    if (e.key === 'Backspace' && !digits[i] && i > 0) inputRefs.current[i - 1]?.focus();
  };

  const handlePaste = (e) => {
    const text = e.clipboardData.getData('text').replace(/[^0-9]/g, '').slice(0, 6);
    if (text.length === 6) {
      e.preventDefault();
      setDigits(text.split(''));
      submitCode(text);
    }
  };

  const submitBackup = (e) => {
    e.preventDefault();
    if (backupCode.trim()) onSubmit(backupCode.trim(), true, remember);
  };

  const linkStyle = { background:'none', border:'none', color:'#888', textDecoration:'underline',
    cursor:'pointer', fontSize:12, padding:0, fontFamily:'inherit' };

  return (
    <div style={{ minHeight:'100vh', display:'flex', alignItems:'center', justifyContent:'center',
      background:'#0a0b0f', fontFamily:'Inter, sans-serif', padding:20 }}>
      <div style={{ width:380, maxWidth:'100%', textAlign:'center' }}>
        <div style={{ display:'flex', justifyContent:'center', marginBottom:16 }}><BrandIcon size={44}/></div>
        <h1 style={{ color:'#fff', fontSize:20, fontWeight:700, margin:0 }}>
          {title || 'Double authentification'}
        </h1>
        <p style={{ color:'#888', fontSize:13, marginTop:8, lineHeight:1.5 }}>
          {subtitle || (useBackup
            ? 'Entrez l\'un de vos codes de secours'
            : 'Entrez le code à 6 chiffres de votre application d\'authentification')}
        </p>

        {!useBackup ? (
          <div style={{ display:'flex', gap:8, justifyContent:'center', margin:'24px 0' }}>
            {digits.map((d, i) => (
              <input key={i} ref={el => inputRefs.current[i] = el}
                type="text" inputMode="numeric" maxLength={1} value={d}
                disabled={loading}
                onChange={e => handleChange(i, e.target.value)}
                onKeyDown={e => handleKeyDown(i, e)}
                onPaste={handlePaste}
                onFocus={e => e.target.select()}
                style={{ width:48, height:56, textAlign:'center', fontSize:22, fontWeight:700,
                  background:'#13141a', border:'1px solid #2a2b35', borderRadius:10, color:'#fff',
                  outline:'none' }}/>
            ))}
          </div>
        ) : (
          <form onSubmit={submitBackup} style={{ margin:'24px 0' }}>
            <input type="text" value={backupCode} onChange={e => setBackupCode(e.target.value)}
              placeholder="XXXX-XXXX" disabled={loading} autoFocus
              style={{ width:'100%', padding:'12px 14px', background:'#13141a', border:'1px solid #2a2b35',
                borderRadius:10, color:'#fff', fontSize:16, textAlign:'center', fontFamily:'monospace',
                outline:'none', boxSizing:'border-box' }}/>
            <button type="submit" disabled={loading || !backupCode.trim()}
              style={{ marginTop:12, width:'100%', padding:'11px', background:'#7c5cfc', border:'none',
                borderRadius:8, color:'#fff', fontSize:14, fontWeight:600, cursor:'pointer' }}>
              {loading ? 'Vérification...' : 'Valider le code de secours'}
            </button>
          </form>
        )}

        {loading && !useBackup && (
          <div style={{ color:'#888', fontSize:12, marginBottom:12 }}>Vérification...</div>
        )}
        {error && (
          <div style={{ color:'#ff4d6d', fontSize:13, marginBottom:12 }}>{error}</div>
        )}

        <label style={{ display:'inline-flex', alignItems:'center', gap:8, padding:'8px 16px',
          background:'#13141a', border:'1px solid #2a2b35', borderRadius:8, cursor:'pointer',
          fontSize:13, color:'#ccc' }}>
          <input type="checkbox" checked={remember} onChange={e => setRemember(e.target.checked)}/>
          Se souvenir de cet appareil
        </label>

        <p style={{ marginTop:20, fontSize:12, color:'#666' }}>
          {!useBackup ? (
            <>Vous pouvez aussi{' '}
              <button type="button" onClick={() => setUseBackup(true)} style={linkStyle}>
                utiliser un code de secours
              </button>
            </>
          ) : (
            <button type="button" onClick={() => setUseBackup(false)} style={linkStyle}>
              Utiliser mon application d'authentification
            </button>
          )}
        </p>

        <button type="button" onClick={onBack}
          style={{ ...linkStyle, fontSize:13, marginTop:8, display:'block', margin:'8px auto 0' }}>
          Retour à la connexion
        </button>
      </div>
    </div>
  );
}
